import { setUserInfo, type MembershipInfo, type OrganizationInfo, type RoleInfo } from '../api/client';

/** Role as the impersonation endpoints return it — the target's real one. */
export interface ImpersonationRole extends RoleInfo {
  type?: 'admin' | 'user';
  isSystem?: boolean;
}

export interface ImpersonationPayload {
  token: string;
  organization: OrganizationInfo;
  user: { _id: string | number; name: string; email: string };
  role?: ImpersonationRole | undefined;
}

/**
 * Where an impersonated session should land. A member dropped on `/admin/*`
 * sees a surface every request 403s.
 */
export const landingPathForRole = (role?: ImpersonationRole | undefined): string =>
  role?.type === 'admin' ? '/admin/calendar' : '/dashboard/calendar';

/**
 * Store an impersonation session.
 *
 * The role stored here is the target's REAL role, and `isSuperAdmin` is false.
 * Both matter: the client used to invent an Owner role (`{ '*': 31 }`) with
 * `isSuperAdmin: true` for every impersonation, so `isAdmin`, `isOwner` and
 * `hasPermission()` were all true whoever was being impersonated — while the
 * backend authorized the same request as the impersonated member. The UI drew
 * the admin surface and every admin route answered 403.
 *
 * `impersonating` stays the marker for "this is an impersonation session", so
 * the switcher and banners key off that rather than off a permission.
 */
export const storeImpersonationSession = (payload: ImpersonationPayload): void => {
  const membership: MembershipInfo = {
    organization: payload.organization,
    role: payload.role as RoleInfo,
    joinedAt: new Date().toISOString(),
    isActive: true,
  };

  setUserInfo({
    _id: String(payload.user._id),
    name: payload.user.name,
    email: payload.user.email,
    memberships: [membership],
    currentMembership: membership,
    isSuperAdmin: false,
    token: payload.token,
  });
  localStorage.setItem('impersonating', 'true');
};
